import { z } from 'zod'

import { PERMISSIONS, type Permission } from './permissions'

export const ROLE_KEYS = [
  'diretoria',
  'coordenacao',
  'cobradora',
  'financeiro',
  'suporte',
  'vendedor',
] as const

export type RoleKey = (typeof ROLE_KEYS)[number]

export const RoleKeySchema = z.enum(ROLE_KEYS, { error: 'Escolha um perfil válido.' })

/**
 * Chave de perfil gravada no banco (Sprint 13: perfis personalizados além dos 5 do sistema).
 * Minúsculas, dígitos e `_`, começando por letra.
 */
export const RoleKeyStringSchema = z
  .string({ error: 'Informe a chave do perfil.' })
  .trim()
  .regex(/^[a-z][a-z0-9_]{1,39}$/, {
    error: 'Use de 2 a 40 letras minúsculas, números ou _, começando por letra.',
  })

/** Verdadeiro para os perfis do sistema (editáveis, não apagáveis, restauráveis; Sprint 17: 6). */
export function isSystemRoleKey(value: string): value is RoleKey {
  return (ROLE_KEYS as readonly string[]).includes(value)
}

export interface RoleDefinition {
  readonly key: RoleKey
  readonly name: string
  readonly permissions: readonly Permission[]
}

const byPrefix = (prefix: string): Permission[] =>
  PERMISSIONS.filter((permission) => permission.startsWith(prefix))

/** Perfis iniciais. O seed grava estes valores na tabela `roles`. */
export const ROLES: readonly RoleDefinition[] = Object.freeze([
  {
    key: 'diretoria',
    name: 'Diretoria',
    permissions: [...PERMISSIONS],
  },
  {
    key: 'coordenacao',
    name: 'Coordenação',
    // Sprint 13: ganha `admin.collectors`; `lgpd.manage` fica com diretoria e suporte.
    // Sprint 11: sem `finance.*` por padrão (editável na matriz).
    permissions: PERMISSIONS.filter(
      (permission) =>
        (!permission.startsWith('admin.') || permission === 'admin.collectors') &&
        !permission.startsWith('finance.') &&
        permission !== 'lgpd.manage',
    ),
  },
  {
    key: 'cobradora',
    name: 'Cobradora',
    permissions: [
      'cases.read',
      'cases.write',
      'customers.read',
      'agreements.write',
      'payments.write',
      'reminders.manage',
      'reports.view',
    ],
  },
  {
    key: 'financeiro',
    name: 'Financeiro',
    permissions: [
      ...byPrefix('finance.'),
      ...byPrefix('reports.'),
      'agreements.approve',
      'customers.read',
      'cases.read',
      'cases.read_all',
    ],
  },
  {
    key: 'suporte',
    name: 'Suporte',
    // Sprint 13: `admin.roles` e `admin.collectors` não entram (só diretoria / coordenação).
    permissions: [
      'admin.users',
      'admin.settings',
      'admin.audit',
      'lgpd.manage',
      'imports.run',
      'customers.merge',
      'templates.manage',
      'cases.read',
      'cases.read_all',
      'customers.read',
      // Sprint 17: vê o funil de todos (sem editar). `sales.read_all` sozinha não abre as rotas,
      // que pedem `sales.read` (decisions.md, 20/10/2026).
      'sales.read',
      'sales.read_all',
    ],
  },
  {
    key: 'vendedor',
    name: 'Vendedor',
    // Sprint 17 (CRM de vendas): só os próprios leads; `customers.read` para o aviso "já é cliente".
    permissions: ['sales.read', 'sales.write', 'reminders.manage', 'customers.read'],
  },
] satisfies RoleDefinition[])

export function getRoleDefinition(key: RoleKey): RoleDefinition {
  const role = ROLES.find((candidate) => candidate.key === key)
  if (!role) throw new Error(`Perfil desconhecido: ${key}`)
  return role
}
